import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import InwardEntryPage from "@/pages/InwardEntryPage";
import InwardLogPage from "@/pages/InwardLogPage";
import OutwardEntryPage from "@/pages/OutwardEntryPage";
import OutwardLogPage from "@/pages/OutwardLogPage";
import LocationsPage from "@/pages/LocationsPage";
import ParcelsPage from "@/pages/ParcelsPage";
import RatesPage from "@/pages/RatesPage";
import ReportsPage from "@/pages/ReportsPage";
import TenantsPage from "@/pages/TenantsPage";
import UsersPage from "@/pages/UsersPage";
import BranchesPage from "@/pages/BranchesPage";
import EmployeesPage from "@/pages/EmployeesPage";
import NotificationsPage from "@/pages/NotificationsPage";
import AuditLogPage from "@/pages/AuditLogPage";
import SettingsPage from "@/pages/SettingsPage";
import ScanPage from "@/pages/ScanPage";
import BranchTransferPage from "@/pages/BranchTransferPage";
import RateMatrixPage from "@/pages/RateMatrixPage";
import PickupsPage from "@/pages/PickupsPage";
import BookingsPage from "@/pages/BookingsPage";
import PlatformAdminPage from "@/pages/PlatformAdminPage";
import MasterCostCentersPage from "@/pages/master/CostCentersPage";
import MasterCompaniesPage from "@/pages/master/CompaniesPage";
import MasterDepartmentsPage from "@/pages/master/DepartmentsPage";
import MasterDeliveryStaffPage from "@/pages/master/DeliveryStaffPage";
import MasterRegulatorsPage from "@/pages/master/RegulatorsPage";
import MasterRoutesPage from "@/pages/master/RoutesPage";
import MasterClientsVendorsPage from "@/pages/master/ClientsVendorsPage";
import CouriersPage from "@/pages/CouriersPage";
import EmailLogsPage from "@/pages/EmailLogsPage";
import HandoffPage from "@/pages/HandoffPage";
import DropWorkflowPage from "@/pages/DropWorkflowPage";
import PublicTrackingPage from "@/pages/PublicTrackingPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import RTOReportPage from "@/pages/RTOReportPage";
import AppLayout from "@/components/layout/AppLayout";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center overline">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // First-login mandatory rotation — block app until password is changed
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  return children;
}

// Delivery Staff Lockdown · Feb 8, 2026 — runners land ONLY on My Deliveries.
// The root `/app` index route uses this to redirect them past the dashboard.
function RoleIndexRedirect() {
  const { user } = useAuth();
  if (user?.role === "delivery_staff") return <Navigate to="drop" replace />;
  return <Navigate to="dashboard" replace />;
}

function ChangePasswordGate() {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center overline">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <ChangePasswordPage forced={!!user.must_change_password} />;
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" toastOptions={{ style: { borderRadius: 8, border: "1px solid hsl(var(--border))" } }} />
          <Routes>
            <Route path="/" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/change-password" element={<ChangePasswordGate />} />
            <Route path="/track" element={<PublicTrackingPage />} />
            <Route path="/track/:tracking_number" element={<PublicTrackingPage />} />
            <Route
              path="/app"
              element={
                <Protected>
                  <AppLayout />
                </Protected>
              }
            >
              <Route index element={<RoleIndexRedirect />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="inward" element={<InwardEntryPage />} />
              <Route path="inward-log" element={<InwardLogPage />} />
              <Route path="outward" element={<OutwardEntryPage />} />
              <Route path="outward-log" element={<OutwardLogPage />} />
              <Route path="locations" element={<LocationsPage />} />
              <Route path="parcels" element={<ParcelsPage />} />
              <Route path="rates" element={<RatesPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="reports/rto" element={<RTOReportPage />} />
              <Route path="tenants" element={<TenantsPage />} />
              <Route path="branches" element={<BranchesPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="employees" element={<EmployeesPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="audit" element={<AuditLogPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="scan" element={<ScanPage />} />
              <Route path="transfer" element={<BranchTransferPage />} />
              <Route path="rate-matrix" element={<RateMatrixPage />} />
              <Route path="pickups" element={<PickupsPage />} />
              <Route path="bookings" element={<BookingsPage />} />
              <Route path="platform-admin" element={<PlatformAdminPage />} />
              <Route path="master/cost-centers" element={<MasterCostCentersPage />} />
              <Route path="master/companies" element={<MasterCompaniesPage />} />
              <Route path="master/departments" element={<MasterDepartmentsPage />} />
              <Route path="master/delivery-staff" element={<MasterDeliveryStaffPage />} />
              <Route path="master/regulators" element={<MasterRegulatorsPage />} />
              <Route path="master/routes" element={<MasterRoutesPage />} />
              <Route path="master/clients-vendors" element={<MasterClientsVendorsPage />} />
              <Route path="couriers" element={<CouriersPage />} />
              <Route path="email-logs" element={<EmailLogsPage />} />
              <Route path="handoff/:id" element={<HandoffPage />} />
              <Route path="drop" element={<DropWorkflowPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
